import { useState, useRef, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useAuth } from '../../context/AuthContext';
import { ChevronDown } from 'lucide-react';
import './TabNavigation.css';

/**
 * TabNavigation Component
 * 
 * Tab-based navigation for main application sections.
 * Tabs with a `children` array render as a dropdown menu.
 */
export default function TabNavigation({ tabs }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isViewer = user?.role === 'viewer';
  const location = useLocation();
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);

  // TEMPORARY: devOnly tabs are only visible to thomas.coyne@tui.co.uk — remove when feature is ready
  const DEV_EMAIL = 'thomas.coyne@tui.co.uk';

  // Filter tabs based on role
  const visibleTabs = tabs.filter(tab => {
    if (tab.devOnly && user?.email !== DEV_EMAIL) return false;
    if (isViewer) return !!tab.viewerVisible;
    if (tab.adminOnly && !isAdmin && !tab.managerVisible) return false;
    if (tab.adminOnly && !isAdmin && tab.managerVisible && isViewer) return false;
    return true;
  }).map(tab => {
    // For sections with children, filter children by role
    if (tab.children && !isAdmin) {
      return {
        ...tab,
        children: tab.children.filter(child => !child.adminOnly)
      };
    }
    return tab;
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on navigation
  useEffect(() => {
    setOpenDropdown(null);
  }, [location.pathname]);

  const isChildActive = (tab) => {
    if (!tab.children) return false;
    return tab.children.some(child => location.pathname.startsWith(child.path));
  };

  return (
    <nav className="tab-navigation">
      <div className="tab-navigation-content">
        <div className="tab-list">
          {visibleTabs.map((tab) => {
            if (tab.children) {
              // Dropdown tab
              const active = isChildActive(tab);
              return (
                <div
                  key={tab.path}
                  className={`tab-dropdown-wrapper ${active ? 'tab-item-active' : ''}`}
                  ref={openDropdown === tab.path ? dropdownRef : null}
                >
                  <button
                    className={`tab-item tab-dropdown-trigger ${active ? 'tab-item-active' : ''}`}
                    onClick={() => setOpenDropdown(openDropdown === tab.path ? null : tab.path)}
                    type="button"
                  >
                    {tab.icon && <span className="tab-icon">{tab.icon}</span>}
                    <span className="tab-label">{tab.label}</span>
                    <ChevronDown size={14} className={`tab-chevron ${openDropdown === tab.path ? 'open' : ''}`} />
                  </button>
                  {openDropdown === tab.path && (
                    <div className="tab-dropdown-menu">
                      {tab.children.map((child) => (
                        <NavLink
                          key={child.path}
                          to={child.path}
                          className={({ isActive }) => `tab-dropdown-item ${isActive ? 'active' : ''}`}
                        >
                          {child.icon && <span className="tab-dropdown-icon">{child.icon}</span>}
                          <span>{child.label}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            // Regular tab
            return (
              <NavLink
                key={tab.path}
                to={tab.path}
                className={({ isActive }) =>
                  `tab-item ${isActive ? 'tab-item-active' : ''}`
                }
              >
                {tab.icon && <span className="tab-icon">{tab.icon}</span>}
                <span className="tab-label">{tab.label}</span>
                {tab.badge && <span className="tab-badge">{tab.badge}</span>}
              </NavLink>
            );
          })}
        </div>
      </div>
    </nav>
  );
}

TabNavigation.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      adminOnly: PropTypes.bool,
      children: PropTypes.arrayOf(
        PropTypes.shape({
          path: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          icon: PropTypes.node,
        })
      )
    })
  ).isRequired
};
